'use client'
import axios from 'axios'
import { Cache } from './cache'
import { md5 } from './crypto'
import qs from 'qs'
import { setupInterceptors } from '@zhiyi/utils'
const $axios = axios.create({
  paramsSerializer: (params) => {
    return qs.stringify(params, { arrayFormat: 'comma' })
  },
})

// 请求拦截器：在发送请求前添加认证信息
$axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('Authorization')
    config.headers.Authorization = token
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)
setupInterceptors($axios)
// 响应拦截器：处理未授权和禁止访问的情况
$axios.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      window.location.href = '/login#' + window.location.pathname
    }
    return Promise.reject(error)
  },
)
const cache = new Cache({
  maxItems: 10,
})
type BackEndResponse<R> = {
  result?: R
  success?: boolean
}

type ResponseType<T> = T extends BackEndResponse<infer R> ? R : T

const handleResponse = <T extends object>(res: { data: T }): ResponseType<T> => {
  if (typeof res.data !== 'object') return res.data as ResponseType<T>
  if ('success' in res.data && res.data.success === false) {
    const errorDesc = ('errorDesc' in res.data ? res.data.errorDesc : '请求失败') as string
    throw new Error(errorDesc)
  }
  const data = 'result' in res.data ? res.data.result : res.data
  return data as ResponseType<T>
}

export const request = {
  cget: async <T extends object>(...args: Parameters<typeof $axios.get>): Promise<ResponseType<T>> => {
    const key = await md5(JSON.stringify(args))
    const cached = cache.get(key)
    if (cached) return cached as ResponseType<T>
    const data = (await request.get.apply(this, args)) as ResponseType<T>
    cache.set(key, data)
    return data
  },
  cpost: async <T extends object>(...args: Parameters<typeof $axios.post>): Promise<ResponseType<T>> => {
    const key = await md5(JSON.stringify(args))
    const cached = cache.get(key)
    if (cached) return cached as ResponseType<T>
    const data = (await request.post.apply(this, args)) as ResponseType<T>
    cache.set(key, data)
    return data
  },
  get: async <T extends object>(...args: Parameters<typeof $axios.get>): Promise<ResponseType<T>> => {
    const res = await $axios.get<T>(...args)
    const data = handleResponse(res)
    return data
  },
  post: async <T extends object>(...args: Parameters<typeof $axios.post>): Promise<ResponseType<T>> => {
    const res = await $axios.post<T>(...args)
    const data = handleResponse(res)
    return data
  },
  put: async <T extends object>(...args: Parameters<typeof $axios.put>): Promise<ResponseType<T>> => {
    const res = await $axios.put<T>(...args)
    return handleResponse(res)
  },
  delete: async <T extends object>(...args: Parameters<typeof $axios.delete>): Promise<ResponseType<T>> => {
    const res = await $axios.delete<T>(...args)
    return handleResponse(res)
  },
  patch: async <T extends object>(...args: Parameters<typeof $axios.patch>): Promise<ResponseType<T>> => {
    const res = await $axios.patch<T>(...args)
    return handleResponse(res)
  },
}
